import { ACTIVITY_EDITOR_VIEW, AUDIENCE_RESTRICTION, BUTTON, POLL_SHARE_MODE, POLL_TITLE_LENGTH, POLL_TYPE, QUESTION_MAX_POINTS, QUESTION_TYPES, SYNC_LMS_OPTIONS, TEXTBOX, USER_SETTINGS } from "../../utils/constants";
import {Modal} from "react-bootstrap";
import YuJaButton from "../standardization/YuJaButton";
import {
    generateDefaultAttendanceTitle,
    generateDefaultPollTitle, getActivityAudienceRestriction,
    getDefaultUserSettings, getQuestionWeightage, isShareByQuestionActivity, notifyError, removePrefix, removePrefixNew,
    transferUserSettings, validateText
} from "../../utils/helpers";
import { ReactComponent as ModalClose } from "../../images/modal_close.svg";
import React, {useEffect, useRef, useState} from "react";
import {useParams} from "react-router-dom";
import {useLocalStorageNew} from "../../hooks/useLocalStorageNew";
import {useSynLMSConfiguration} from "../../hooks/useSynLMSConfiguration";
import {
    addQuestion,
    questionError,
    setBlankRestriction,
    trimLineBreakerInQuestion,
    trimQuestion
} from "../../utils/questionUtils";
import {CourseApiCalls} from "../../apis/CourseApiCalls";
import {PollApiCalls} from "../../apis/PollApiCalls";
import classes from "../../pages/CreateQuizPage/ActivityEditor.module.css";
import ActivityEditorTitleContainer from "../../pages/CreateQuizPage/ActivityEditorTitleContainer";
import ActivityEditorDashboard from "../../pages/CreateQuizPage/ActivityEditorDashboard";
import ActivityEditorMainView from "../../pages/CreateQuizPage/ActivityEditorMainView";
import {ReportApiCalls} from "../../apis/ReportApiCalls";
import {YuJaTooltip} from "../standardization/YuJaTooltip";
import YuJaDialog from "../standardization/YuJaDialog";
import ActivityEditorImportQuestionsModal from "../../pages/CreateQuizPage/ActivityEditorImportQuestionsModal";
import {Dialog} from "@base-ui/react/dialog";
import styles from "../standardization/YuJaDialog.module.css";
import {Button} from "@base-ui/react/button";
import {Separator} from "@base-ui/react/separator";
import { useTranslation } from "react-i18next";

export default function EditActivityModal({show, setShow, publishedData, reloadPollData}) {
    const { t } = useTranslation();
    const { pollKey, pollCode } = useParams();
    const [, getSession] = useLocalStorageNew("session", {});
    const session = getSession();

    const {
        correctConfigurations,
        verifyConfiguration,
        isLMS,
        verifyConfigurationBeforeSaving
    } = useSynLMSConfiguration();
    const {getCourseByUser} = CourseApiCalls();
    const {updateAndPublish, getPollDataFromKey} = PollApiCalls();
    const {warmUpBrowser, takeScreenshotsNew} = ReportApiCalls();

    const [userSettings, setUserSettings] = useState(getDefaultUserSettings());
    const [currQueDisplay, setCurrQueDisplay] = useState(1);

    const [focusMCElem, setFocusMCElem] = useState(-1);
    const [focusSAElem, setFocusSAElem] = useState(-1);
    const [focusTFElem, setFocusTFElem] = useState(-1);
    const [focusFITBElem, setFocusFITBElem] = useState(-1);
    const [focusCIElem, setFocusCIElem] = useState(-1);
    const [focusMHElem, setFocusMHElem] = useState(-1);
    const [focusRKElem, setFocusRKElem] = useState(-1);
    const [isSaving, setIsSaving] = useState(false);

    const [dataLoading, setDataLoading] = useState(true);
    const [savedActivity, setSavedActivity] = useState({});
    const [draftActivity, setDraftActivity] = useState({});
    const [editorView, setEditorView] = useState(ACTIVITY_EDITOR_VIEW.QUESTION_EDITING);
    const [courseList, setCourseList] = useState([]);
    const [hasReleased, setHasReleased] = useState(false);
    const [isImportQuestionModalShown, setIsImportQuestionModalShown] = useState(false);
    const parentRef = useRef();


    const {poll: publishedActivity, questions: publishedQuestions} = publishedData;
    const {pollType} = publishedActivity;

    const setQuestions = (questions) => {
        const copy = JSON.parse(JSON.stringify(draftActivity));
        copy.questions = questions;
        setDraftActivity(copy);
    }

    const loadCourse = async () => {
        const {idpType="", intType=""} = session;
        const courses = await getCourseByUser(idpType, intType);
        const options = courses.map(course => ({
            value: course.courseId,
            label: course.courseName,
            code: course.courseCode,
            isLmsCourse: !!course.index3Pk,
            lmsCourseId: removePrefixNew(!!course.index3Pk ? course.index3Pk : "")
        }));
        setCourseList(options);

        return options;
    }

    const checkChanged = () => {
        const draftStr = JSON.stringify(draftActivity);
        const savedStr = JSON.stringify(savedActivity);
        return draftStr !== savedStr;
    }

    const generateDefaultTitle = () => {
        if (draftActivity.pollType === POLL_TYPE.ATTENDANCE) {
            return generateDefaultAttendanceTitle();
        } else {
            return generateDefaultPollTitle();
        }
    }

    const validatePoll = (draft, focusError = true) => {
        if (!validateText(draft.pollTitle, POLL_TITLE_LENGTH)) {
            if (focusError) {
                notifyError(t("activity.titleError"));
                // document.getElementsByName("pollTitle")[0].focus();
            }
            return false;
        }

        if (draft.pollType !== POLL_TYPE.ATTENDANCE && !draft.hasOwnProperty("gradeSchema")) {
            notifyError(t("activity.typeMissingError"));
            setEditorView(ACTIVITY_EDITOR_VIEW.OVERVIEW);
            return false;
        }

        if (draft.syncLmsOption !== SYNC_LMS_OPTIONS.EMBEDDED
            && draft.audienceRestriction === AUDIENCE_RESTRICTION.REGISTERED
            && !draft.courseId
        ) {
            setEditorView(ACTIVITY_EDITOR_VIEW.CONFIGURATION);
            notifyError(t("sharePoll.selectCourseMessage"));
            return false;
        }

        let i = 0;
        for (let q of draft.questions) {
            i++;
            if (getQuestionWeightage(q) > QUESTION_MAX_POINTS) {
                if (focusError) {
                    let errorWithQuestion = t("accessibility.questionPrefix") + " " + i + ": " + t("questionBuilder.maxPointsExceeded");
                    notifyError(errorWithQuestion);
                    setCurrQueDisplay(q.serialNo);
                    setEditorView(draft.pollType === POLL_TYPE.ATTENDANCE
                        ? ACTIVITY_EDITOR_VIEW.ATTENDANCE_EDITING
                        : ACTIVITY_EDITOR_VIEW.QUESTION_EDITING
                    );
                }
                return false;
            }
        }

        let index = 0;
        for (let q of draft.questions) {
            index++;
            if (focusError) {
                setCurrQueDisplay(q.serialNo);
            }
            let {quesError, elemIndex} = questionError(q, draft.pollType, draft.gradeSchema);
            switch (q.questionType) {
                case QUESTION_TYPES.MCSS.name:
                    if (focusError) {
                        setFocusMCElem(elemIndex);
                    }
                    break;
                case QUESTION_TYPES.SA.name:
                case QUESTION_TYPES.IB.name:
                case QUESTION_TYPES.RETRO.name:
                case QUESTION_TYPES.BS.name:
                    if (focusError) {
                        setFocusSAElem(elemIndex);
                    }
                    break;
                case QUESTION_TYPES.TF.name:
                    if (focusError) {
                        setFocusTFElem(elemIndex);
                    }
                    break;
                case QUESTION_TYPES.FITB.name:
                    if (focusError) {
                        setTimeout(() => {
                            setFocusFITBElem(elemIndex);
                        }, 100);
                    }
                    break;
                case QUESTION_TYPES.CI.name:
                    if (focusError) {
                        setFocusCIElem(elemIndex);
                    }
                case QUESTION_TYPES.MH.name:
                    if (focusError) {
                        setFocusMHElem(elemIndex);
                    }
                    break;
                case QUESTION_TYPES.RK.name:
                    if (focusError) {
                        setFocusRKElem(elemIndex);
                    }
                    break;
            }

            if (quesError) {
                if (focusError) {
                    let errorWithQuestion = t("accessibility.questionPrefix") + " " + index + ": " + quesError;
                    // setQuestions(chooseQuestion(q.serialNo, questions));
                    setCurrQueDisplay(q.serialNo);
                    setEditorView(draft.pollType === POLL_TYPE.ATTENDANCE
                        ? ACTIVITY_EDITOR_VIEW.ATTENDANCE_EDITING
                        : ACTIVITY_EDITOR_VIEW.QUESTION_EDITING
                    );
                    notifyError(errorWithQuestion);
                }
                return false;
            }
        }
        return true;
    };


    const editPollClose = async () => {
        let pollToAdd = JSON.parse(JSON.stringify(draftActivity));
        let questionsCopy = pollToAdd.questions;

        if (pollToAdd.pollType !== POLL_TYPE.ATTENDANCE) {
            questionsCopy = trimQuestion(questionsCopy, draftActivity.gradeSchema);
        }

        for (const que of questionsCopy) {
            trimLineBreakerInQuestion(que);
        }
        pollToAdd.questions = questionsCopy;
        pollToAdd = correctConfigurations(pollToAdd);

        if (!pollToAdd.questions.length) {
            notifyError(t("questionBuilder.addQuestionWithType"));
            return;
        }


        if (!validatePoll(pollToAdd)) {
            return;
        }

        let error = verifyConfiguration(pollToAdd);
        if (!!error) {
            notifyError(error);
            setEditorView(ACTIVITY_EDITOR_VIEW.OVERVIEW);
            return;
        }

        setIsSaving(true);
        updateAndPublish(pollToAdd, true, true).then((result) => {
            if (!result) {
                return;
            }

            let {draft={}, published={}} = result;
            published = !!published ? published : {};
            const {isValid, pk, sk, totalQuestions} = published;

            if (!!isValid) {
                takeScreenshotsNew(
                    removePrefixNew(pk),
                    removePrefixNew(sk),
                    isShareByQuestionActivity(pollToAdd),
                    Array.from({ length: totalQuestions }, (_, i) => ({ serialNo: i + 1 }))
                );
            }

            pollToAdd.syncLms = draft.syncLms;
            pollToAdd.syncLmsOption = draft.syncLmsOption;
            pollToAdd.lmsHomeUrl = draft.lmsHomeUrl;
            pollToAdd.audienceRestriction = draft.audienceRestriction;
            pollToAdd.courseId = removePrefix(draft.courseId);

            setSavedActivity(pollToAdd);
            setDraftActivity(JSON.parse(JSON.stringify(pollToAdd)));
            setEditorView(ACTIVITY_EDITOR_VIEW.SAVE_SUCCESS);
            reloadPollData();
            onClose();
        }).finally(() => {
            setIsSaving(false);
        });
        // history.push(POLL_PATH);
    };


    useEffect(() => {
        if (!show) {
            return;
        }

        let {settings = {}} = session;
        settings = transferUserSettings(settings);
        setUserSettings(settings);
        setDataLoading(true);
        warmUpBrowser("", "", "").then();
        const promise1 = getPollDataFromKey(pollKey);

        const promise2 =  loadCourse();

        Promise.all([promise1, promise2]).then(async ([res, res2]) => {
            for (const question of res.questions) {
                if (question.questionType === QUESTION_TYPES.MH.name && !question.correctAnswers) {
                    question.correctAnswers = question.optionsMap;
                }
            }

            let activity = {};
            activity.pollKey = pollKey;
            activity.pollTitle = !!res.pollTitle ? res.pollTitle : generateDefaultTitle();
            activity.pollType = res.pollType;
            activity.gradeSchema = res.gradeSchema;
            activity.pollShareMode = res.hasOwnProperty("pollShareMode") ? res.pollShareMode : publishedActivity.pollShareMode;
            activity.pollDescription = res.pollDescription;
            activity.pollTimeLimit = publishedActivity.pollTimeLimit !== res.pollTimeLimit ? publishedActivity.pollTimeLimit : res.pollTimeLimit;
            activity.pollBreakDuration = res.hasOwnProperty("pollBreakDuration") ? res.pollBreakDuration : settings[USER_SETTINGS.POLL_BREAK_DURATION];
            activity.getReadyAllTimeLimit = res.hasOwnProperty("getReadyAllTimeLimit") ? res.getReadyAllTimeLimit : settings[USER_SETTINGS.POLL_COUNTDOWN];
            activity.recordAttempt = res.hasOwnProperty("recordAttempt") ? res.recordAttempt : settings[USER_SETTINGS.POLL_RECORD_ATTEMPTS];
            activity.lmsAttempt = res.hasOwnProperty("lmsAttempt") ? res.lmsAttempt : settings[USER_SETTINGS.POLL_LMS_SYNC];
            activity.maxAttempts = res.hasOwnProperty("maxAttempts") ? res.maxAttempts : settings[USER_SETTINGS.POLL_MAX_ATTEMPTS];
            activity.showAnswerOnViewer = res.hasOwnProperty("showAnswerOnViewer") ? res.showAnswerOnViewer : settings[USER_SETTINGS.POLL_SHOW_CORRECT_ANS];
            activity.audienceRestriction = getActivityAudienceRestriction(res, settings);
            activity.pptAutoShare = res.hasOwnProperty("pptAutoShare") ? res.pptAutoShare : settings[USER_SETTINGS.PPT_AUTO_SHARE];
            activity.manualEnd = publishedActivity.manualEnd !== res.manualEnd ? publishedActivity.manualEnd : res.manualEnd;
            activity.lmsHomeUrl = res.lmsHomeUrl;
            activity.LMSColumnId = res.LMSColumnId;
            activity.lmsRedirectUrl = res.lmsRedirectUrl;
            activity.D2LModuleId = res.D2LModuleId;
            activity.moodleSectionId = res.moodleSectionId;
            activity.courseId = removePrefix(res.courseId);

            if (res.hasOwnProperty("syncLms")) {
                activity.syncLms = res.syncLms;
                activity.syncLmsOption = res.syncLmsOption;
            }

            // let defaultModuleId = await loadModulesAndGetDefaultId(activity.courseId);
            //
            // if (!activity.D2LModuleId || activity.D2LModuleId === "-1") {
            //     activity.D2LModuleId = defaultModuleId;
            //
            // }

            let hasReleased = false;
            if (publishedActivity.pollShareMode === POLL_SHARE_MODE.SCHEDULE) {
                hasReleased = (Date.now() - new Date(publishedActivity.startTime).getTime()) > 0;
            } else {
                for (const q of publishedQuestions) {
                    if (!!q.shareCount) {
                        hasReleased = true;
                        break;
                    }
                }
            }

            setHasReleased(hasReleased);




            if (res.pollType === POLL_TYPE.ATTENDANCE) {
                if (!res.hasOwnProperty("questions") || !res.questions.length || res.questions[0].questionType !== QUESTION_TYPES.SA.name) {
                    const qs = addQuestion([], QUESTION_TYPES.SA.name, userSettings, false, POLL_TYPE.ATTENDANCE);
                    activity.questions = qs;
                } else {
                    activity.questions = res.questions;
                }
                activity.geofence = res.hasOwnProperty("geofence") ? res.geofence : {};
                activity.attendanceDuration = res.hasOwnProperty("attendanceDuration") ? res.attendanceDuration : settings[USER_SETTINGS.ATTENDANCE_DURATION];


                setEditorView(hasReleased
                    ? (isLMS() ? ACTIVITY_EDITOR_VIEW.OVERVIEW : ACTIVITY_EDITOR_VIEW.CONFIGURATION)
                    : ACTIVITY_EDITOR_VIEW.ATTENDANCE_EDITING
                );
            } else {
                setBlankRestriction(res.questions);
                activity.questions = !!res.hasOwnProperty("questions") && !!res.questions.length ? res.questions : [{serialNo: 1}];

                setEditorView(hasReleased ? ACTIVITY_EDITOR_VIEW.OVERVIEW : ACTIVITY_EDITOR_VIEW.QUESTION_EDITING);
            }

            for (const q of activity.questions) {
                let publishedQuestion = publishedQuestions.find(que => que.serialNo === q.serialNo);
                if (publishedActivity.pollShareMode === POLL_SHARE_MODE.SCHEDULE && hasReleased) {
                    q.shareCount = 1;
                } else if (!!publishedQuestion) {
                    q.shareCount = publishedQuestion.shareCount;
                }
            }


            if (activity.pollShareMode === POLL_SHARE_MODE.SHARE_EACH && activity.pollType !== POLL_TYPE.ATTENDANCE) {
                let unsharedQuestions = activity.questions.filter(q => !q.shareCount);
                if (unsharedQuestions.length > 0) {
                    setCurrQueDisplay(unsharedQuestions[0].serialNo);
                } else {
                    let newSerialNo = activity.questions.length + 1;
                    setCurrQueDisplay(newSerialNo);
                    activity.questions.push({serialNo: newSerialNo});
                }

                setEditorView(ACTIVITY_EDITOR_VIEW.QUESTION_EDITING);
            }


            setSavedActivity(activity);
            setDraftActivity(JSON.parse(JSON.stringify(activity)));
        }).finally(() => setDataLoading(false));
    }, [publishedActivity, publishedQuestions, show]);





    // useEffect(() => {
    //     if (!show) {
    //         return;
    //     }
    //
    //     let hasReleased = false;
    //     const {startTime,  pollShareMode} = publishedActivity;
    //     if (pollShareMode === POLL_SHARE_MODE.SCHEDULE) {
    //         hasReleased = (Date.now() - new Date(startTime).getTime()) > 0;
    //         if (hasReleased && editorView === ACTIVITY_EDITOR_VIEW.QUESTION_EDITING) {
    //             setEditorView(ACTIVITY_EDITOR_VIEW.OVERVIEW);
    //         }
    //     } else {
    //         for (const q of publishedQuestions) {
    //             if (!!q.shareCount) {
    //                 hasReleased = true;
    //                 break;
    //             }
    //         }
    //     }
    //
    //     setHasReleased(hasReleased);
    // }, [publishedActivity, publishedQuestions, show]);

    const getSaveBtnTooltipText = () => {
        let errorText = verifyConfigurationBeforeSaving(draftActivity);
        if (!!errorText) {
            return errorText;
        }

        if (!draftActivity.questions
            || !draftActivity.questions.length
            || !draftActivity.questions.filter(q => !!q.questionType).length
        ) {
            return "Add a question before saving.";
        }
    }

    const onClose = () => {
        setShow(false);
        setEditorView(ACTIVITY_EDITOR_VIEW.QUESTION_EDITING);
    }


    let saveBtnTooltipText = getSaveBtnTooltipText();
    return (
        <Dialog.Root
            open={show}
            disablePointerDismissal={true}
            onOpenChange={(open) => {
                if (!open) {
                    onClose();
                }
            }}
        >
            <Dialog.Portal>
                <Dialog.Backdrop className={styles.Backdrop}/>
                <Dialog.Viewport className={styles.Viewport} style={{justifyContent: "start"}}>
                    <Dialog.Popup className={styles.Popup} style={{width: "min(1200px, calc(100vw - 2rem))"}}>
                        <Dialog.Title className={styles.Title}>
                            {editorView === ACTIVITY_EDITOR_VIEW.IMPORT_QUESTIONS
                                ? t("questionBank.importQuestionsText")
                                : (pollType === POLL_TYPE.ATTENDANCE ? t("attendance.editAttendanceText") : t("poll.editPollText"))

                            }

                            <YuJaTooltip text={t("poll.closeLabel")}>
                                <Button
                                    className={styles.CloseIconBtn}
                                    onClick={() => onClose()}
                                >
                                    <CloseIcon/>
                                </Button>
                            </YuJaTooltip>



                        </Dialog.Title>
                        <Separator orientation={"horizontal"} className={styles.Separator}/>

                        {editorView === ACTIVITY_EDITOR_VIEW.IMPORT_QUESTIONS &&
                            <ActivityEditorImportQuestionsModal
                                parentRef={parentRef}
                                open={editorView === ACTIVITY_EDITOR_VIEW.IMPORT_QUESTIONS}
                                setEditorView={setEditorView}
                                draftActivity={draftActivity}
                                setDraftActivity={setDraftActivity}
                                courseList={courseList}
                                userSettings={userSettings}
                            />
                        }
                        {editorView !== ACTIVITY_EDITOR_VIEW.IMPORT_QUESTIONS &&
                            <div className={styles.Body}>
                                <div className={`${classes.parentContainer} ${classes.gridLayout}`} style={{gridTemplateRows: "auto minmax(300px, 1fr)"}}>
                                    <ActivityEditorTitleContainer
                                        generateTitle={generateDefaultTitle}
                                        dataLoading={dataLoading}
                                        draftActivity={draftActivity}
                                        setDraftActivity={setDraftActivity}
                                        userSettings={userSettings}
                                        currQueDisplay={currQueDisplay}
                                        setCurrQueDisplay={setCurrQueDisplay}
                                        isAutoSaving={null}
                                        isSaving={isSaving}
                                        saveBtnDisabled={null}
                                        handleSave={editPollClose}
                                        hasReleased={hasReleased}
                                        editorView={editorView}
                                        setEditorView={setEditorView}
                                    />

                                    <ActivityEditorDashboard
                                        dataLoading={dataLoading}
                                        editorView={editorView}
                                        setEditorView={setEditorView}
                                        draftActivity={draftActivity}
                                        setQuestions={setQuestions}
                                        currQueDisplay={currQueDisplay}
                                        setCurrQueDisplay={setCurrQueDisplay}
                                        userSettings={userSettings}
                                        hasReleased={hasReleased}
                                    />
                                    <ActivityEditorMainView
                                        userSettings={userSettings}
                                        dataLoading={dataLoading}
                                        editorView={editorView}
                                        setEditorView={setEditorView}
                                        draftActivity={draftActivity}
                                        setDraftActivity={setDraftActivity}
                                        currQueDisplay={currQueDisplay}
                                        setCurrQueDisplay={setCurrQueDisplay}
                                        courseList={courseList}
                                        hasReleased={hasReleased}
                                    />
                                </div>
                            </div>
                        }

                        {editorView !== ACTIVITY_EDITOR_VIEW.IMPORT_QUESTIONS &&
                            <div className={styles.Actions}>
                                <YuJaButton
                                    size={"small"}
                                    type={"secondary"}
                                    onClick={onClose}
                                >
                                    {t("common.cancel")}
                                </YuJaButton>

                                <YuJaButton
                                    size={"small"}
                                    disabled={!checkChanged() || !!saveBtnTooltipText || isSaving}
                                    onClick={editPollClose}
                                >{t("institution.saveChangesText")}</YuJaButton>
                            </div>
                        }
                    </Dialog.Popup>
                </Dialog.Viewport >
            </Dialog.Portal>
        </Dialog.Root>
    );
}

function CloseIcon(props) {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.75 5.25L5.25 18.75" stroke="#6B6B6B" strokeWidth="1.5" strokeLinecap="round"
                  strokeLinejoin="round"/>
            <path d="M18.75 18.75L5.25 5.25" stroke="#6B6B6B" strokeWidth="1.5" strokeLinecap="round"
                  strokeLinejoin="round"/>
        </svg>

    );
}